// src/components/Town/utils/fetchRevenueData.js

import { getDoc, doc } from "firebase/firestore";
import { db } from "../../../firebase";

/**
 * Summary tables expect fetchRevenueData(category) to return:
 * {
 *   sections: [
 *     { name: "Some Section Header", rows: [ { label, y0_adopted, ... }, ... ] },
 *     ...
 *   ]
 * }
 *
 * New Firestore shape:
 * townGovernment/<docId> where docId is like:
 *   - revenues_tax_revenues
 *   - revenues_licenses_permits
 *   - revenues_intergovernmental
 *   - revenues_charges_for_services
 *   - revenues_other_revenues
 *   - revenues_transfers_in_from
 *
 * And each year looks like:
 * "2020": {
 *   "Approved": { "<BlockName>": { "<Item>": 123, ... }, "Total": 999 },
 *   "Actual":   { "<BlockName>": { "<Item>": 456, ... }, "Total": 888 }
 * }
 * (Intergovernmental has multiple blocks under Approved/Actual.)
 */

const DOC_ID_BY_CATEGORY = {
  taxes: "revenues_tax_revenues",
  licensesAndPermits: "revenues_licenses_permits",
  intergovernmental: "revenues_intergovernmental",
  chargesForServices: "revenues_charges_for_services",
  otherRevenues: "revenues_other_revenues",
  transfersIn: "revenues_transfers_in_from",
};

const WRAPPER_LABELS = {
  taxes: "Tax Revenues",
  licensesAndPermits: "Licenses & Permits",
  chargesForServices: "Charges for Services",
  otherRevenues: "Other Revenues",
  transfersIn: "Transfers In From",
  intergovernmental: "Intergovernmental",
};

function formatLabel(key) {
  return String(key)
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/^./, (str) => str.toUpperCase());
}

function typeToSectionKey(type) {
  // Your columns use: adopted | actual
  // Your docs use: Approved | Actual
  if (type === "adopted") return "Approved";
  if (type === "actual") return "Actual";
  // Future-proofing if you ever add proposed/projected/etc
  return null;
}

/**
 * Extract rows for a single (year,type) column from the new per-category doc.
 * Returns entries in the shape:
 *   [{ sectionName, label, value }, ...]
 */
function extractYearItemsFromCategoryDoc(docData, year, category, type) {
  const yearNode = docData?.[String(year)];
  if (!yearNode) return [];

  const sectionKey = typeToSectionKey(type);
  if (!sectionKey) return [];

  const sectionNode = yearNode?.[sectionKey];
  if (!sectionNode || typeof sectionNode !== "object") return [];

  // Remove "Total" (case-insensitive)
  const nonTotalEntries = Object.entries(sectionNode).filter(
    ([k]) => !/^total$/i.test(String(k))
  );

  // If entries are nested objects => blocks (e.g., "Tax Revenues": {...})
  const nestedBlocks = nonTotalEntries.filter(
    ([, v]) => v && typeof v === "object" && !Array.isArray(v)
  );

  const results = [];

  if (nestedBlocks.length > 0) {
    for (const [blockName, blockItems] of nestedBlocks) {
      for (const [label, value] of Object.entries(blockItems || {})) {
        results.push({
          sectionName: blockName,
          label,
          value: value ?? null,
        });
      }
    }
    return results;
  }

  // Flat fallback: treat sectionNode itself as items
  const wrapperName =
    docData?.fullName ||
    WRAPPER_LABELS[category] ||
    formatLabel(category || "Category");

  for (const [label, value] of nonTotalEntries) {
    results.push({
      sectionName: wrapperName,
      label,
      value: value ?? null,
    });
  }

  return results;
}

export async function fetchRevenueData(category, baseColumns) {
  const docId =
    DOC_ID_BY_CATEGORY[category] ||
    // conservative fallback: if you pass a docId directly
    (typeof category === "string" && category.startsWith("revenues_")
      ? category
      : null);

  if (!docId) {
    console.warn("fetchRevenueData: unknown category/docId:", category);
    return { sections: [], rawDoc: {} };
  }

  const snap = await getDoc(doc(db, "townGovernment", docId));
  if (!snap.exists()) {
    console.warn(`fetchRevenueData: missing doc townGovernment/${docId}`);
    return { sections: [], rawDoc: {} };
  }

  const docData = snap.data();
  const sectionsMap = {};

  // baseColumns[0] is the label column; skip it
  for (const col of (baseColumns || []).slice(1)) {
    const { year, type, key } = col;
    if (!year || !type || !key) continue;

    const entries = extractYearItemsFromCategoryDoc(docData, year, category, type);

    for (const { sectionName, label, value } of entries) {
      if (!sectionsMap[sectionName]) sectionsMap[sectionName] = {};
      if (!sectionsMap[sectionName][label]) {
        sectionsMap[sectionName][label] = { label };
      }

      sectionsMap[sectionName][label][key] =
        typeof value === "number" && Number.isFinite(value) ? value : value ?? null;
    }
  }

  const sections = Object.entries(sectionsMap).map(([name, rowsObj]) => ({
    name,
    rows: Object.values(rowsObj),
  }));

  return { sections, rawDoc: docData };
}
